import {keyToCoords} from "../../coords/keyToCoords";
import {getHexesOnLine} from "../../coords/getHexesOnLine";
import {HexMath} from "../../HexMath";
import {UNIT_D4_MAX_SHOOTING_RANGE} from "../../../constants/units";
import {CITY_COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS, TERRAIN_TOKENS} from "../../../constants/tokens";
import {FORTRESS_MAX_DAMAGE} from "../../../configs/specialBuildings";
import {isBuildingDestroyed} from "./buildingDamageUtils";

const CITY_BUILDING_PROPERTIES = [
  'centralQuarter',
  ...Object.values(CITY_COMMON_BUILDING_TOKENS),
  ...Object.values(SPECIAL_BUILDING_TOKENS),
];

/**
 * Собирает все hexKeys строений вражеских городов (исключая ключевые точки: рудники, руины)
 * @param {Object} citiesCache - кэш городов
 * @param {Object} currentPlayer - текущий игрок
 * @param {Object} specialBuildings - специальные строения
 * @param {Object} buildingsDamage - уже применённые повреждения
 * @param {Array} pendingShootingActions - ожидающие действия стрельбы
 * @param hexes
 * @returns {Set<string>} множество hexKeys вражеских городских строений
 */
const getEnemyCityBuildingHexKeys = (citiesCache, currentPlayer, specialBuildings, buildingsDamage, pendingShootingActions, hexes) => {
  const hexKeys = new Set();

  Object.entries(citiesCache).forEach(([playerColor, cities]) => {
    // Не стреляем по своим городам
    if (playerColor === currentPlayer.color) return;

    cities.forEach(city => {
      // Центральный квартал защищён только пока есть хотя бы один не разрушенный обычный квартал
      const hasIntactOrdinaryQuarters = city.quarter && city.quarter.some(qHexKey =>
        !isBuildingDestroyed(qHexKey, specialBuildings, buildingsDamage, pendingShootingActions, hexes)
      );

      CITY_BUILDING_PROPERTIES.forEach(property => {
        // Если в городе есть не разрушенные обычные кварталы, центральный квартал недоступен для стрельбы
        if (property === 'centralQuarter' && hasIntactOrdinaryQuarters) {
          return;
        }

        const value = city[property];
        if (!value) return;

        const addHexKey = (hexKey) => {
          // Не добавляем разрушенные строения
          if (isBuildingDestroyed(hexKey, specialBuildings, buildingsDamage, pendingShootingActions, hexes)) {
            return;
          }
          hexKeys.add(hexKey);
        };

        if (Array.isArray(value)) {
          value.forEach(addHexKey);
        } else if (typeof value === 'string') {
          addHexKey(value);
        }
      });
    });
  });

  return hexKeys;
};

/**
 * Проверяет, есть ли препятствие (гора или крепость) на линии траектории между стрелком и целью.
 * Крепость считается препятствием только если она не в строительстве и не будет уничтожена
 * уже нанесённым в рамках текущего этапа уроном.
 * @param {string} shooterHexKey - hexKey стреляющего
 * @param {string} targetHexKey - hexKey цели
 * @param {Object} hexes - состояние гексов
 * @param {Object} specialBuildings - специальные строения
 * @param {Array} pendingShootingActions - ожидающие действия стрельбы
 * @param {Object} buildingsDamage - уже применённые повреждения строений
 * @param fortressesUnderConstruction
 * @returns {boolean}
 */
export const hasObstacleInPath = (shooterHexKey, targetHexKey, hexes, specialBuildings, pendingShootingActions, buildingsDamage, fortressesUnderConstruction) => {
  const [shooterQ, shooterR] = keyToCoords(shooterHexKey);
  const [targetQ, targetR] = keyToCoords(targetHexKey);

  const lineHexes = getHexesOnLine(shooterQ, shooterR, targetQ, targetR);

  return lineHexes.some(hexKey => {
    if (hexKey === shooterHexKey || hexKey === targetHexKey) {
      return false;
    }
    if (hexes[hexKey] === TERRAIN_TOKENS.MOUNTAIN) {
      return true;
    }
    const isFortress = specialBuildings?.[hexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS;
    const isUnderConstruction = fortressesUnderConstruction?.includes(hexKey);
    if (!isFortress || isUnderConstruction) {
      return false;
    }
    // Крепость будет препятствием только если она НЕ будет уничтожена текущим pending уроном
    const existingDamage = buildingsDamage?.[hexKey] || 0;
    const pendingDamage = (pendingShootingActions || [])
      .filter(action => action.targetHexKey === hexKey)
      .reduce((sum, action) => sum + (action.damage || 0), 0);
    const willBeDestroyed = existingDamage + pendingDamage >= FORTRESS_MAX_DAMAGE;
    return !willBeDestroyed;
  });
};

/**
 * Рассчитывает доступные для стрельбы ячейки с вражескими отрядами и строениями городов
 * @param {Object} hexes - состояние гексов
 * @param {Array} units - все отряды
 * @param {Object} selectedUnit - выбранный отряд (катапульта)
 * @param {Array} pendingShootingActions - ожидающие действия стрельбы
 * @param {Object} citiesCache - кэш городов
 * @param {Object} currentPlayer - текущий игрок
 * @param {Object} specialBuildings - специальные строения
 * @param {Object} buildingsDamage - уже применённые повреждения строений
 * @param fortressesUnderConstruction
 * @returns {Array} список hexKey доступных целей
 */
export const calculateAvailableShootingCells = (
  hexes,
  units,
  selectedUnit,
  pendingShootingActions,
  citiesCache,
  currentPlayer,
  specialBuildings,
  buildingsDamage,
  fortressesUnderConstruction,
) => {
  // Проверяем не выстрелил ли отряд уже в этом этапе
  const alreadyShot = pendingShootingActions.some(
    action => action.shooterUnitId === selectedUnit.id
  );
  if (alreadyShot) {
    return [];
  }

  const hexMath = new HexMath(30, 'flat');

  const [selectedQ, selectedR] = keyToCoords(selectedUnit.hexKey);
  const shooterOwnerId = selectedUnit.ownerId;

  const targetCells = [];
  const addedTargets = new Set();

  // --- Цели: вражеские отряды ---
  units.forEach(unit => {
    // Пропускаем отряды того же игрока
    if (unit.ownerId === shooterOwnerId) {
      return;
    }

    // Считаем уже полученный урон по отряду
    const existingDamage = pendingShootingActions
      .filter(action => action.targetUnitId === unit.id)
      .reduce((sum, action) => sum + action.damage, 0);

    // Пропускаем отряды которые уже будут уничтожены по суммарному урону
    if (unit.size <= existingDamage) {
      return;
    }

    const [targetQ, targetR] = keyToCoords(unit.hexKey);
    const distance = hexMath.hexDistance(selectedQ, selectedR, targetQ, targetR);

    if (distance <= UNIT_D4_MAX_SHOOTING_RANGE) {
      if (!hasObstacleInPath(selectedUnit.hexKey, unit.hexKey, hexes, specialBuildings, pendingShootingActions, buildingsDamage, fortressesUnderConstruction)) {
        targetCells.push(unit.hexKey);
        addedTargets.add(unit.hexKey);
      }
    }
  });

  // --- Цели: строения вражеских городов ---
  if (citiesCache && currentPlayer) {
    const enemyBuildingHexKeys = getEnemyCityBuildingHexKeys(
      citiesCache,
      currentPlayer,
      specialBuildings,
      buildingsDamage,
      pendingShootingActions,
      hexes,
    );

    enemyBuildingHexKeys.forEach(hexKey => {
      // Не добавляем дубликаты (если на клетке уже есть отряд)
      if (addedTargets.has(hexKey)) return;

      const [targetQ, targetR] = keyToCoords(hexKey);
      const distance = hexMath.hexDistance(selectedQ, selectedR, targetQ, targetR);

      if (distance <= UNIT_D4_MAX_SHOOTING_RANGE) {
        if (!hasObstacleInPath(selectedUnit.hexKey, hexKey, hexes, specialBuildings, pendingShootingActions, buildingsDamage, fortressesUnderConstruction)) {
          targetCells.push(hexKey);
          addedTargets.add(hexKey);
        }
      }
    });
  }

  return targetCells;
};
